import { useCallback } from 'react';

import { useAIAnswerContext } from '../../../../ai-answer-dialog/ai-answer-context';
import { NLPSearchResultFormat } from '../../../../ai-answer-dialog/types';
import { fetchAggResponse } from '../../../../common';
import { createAtlAttributionHeader } from '../../../../common/fetch/atl-attribution-header';
import {
	CreateDefinitionMutation,
	type CreateDefinitionMutationType,
	type CreateDefinitionMutationVariables,
} from '../../gql/create-definition-mutation';
import type {
	CreateDefinitionFunctionArgs,
	CreateDefinitionFunctionType,
	CuratedDefinitionScopeConfigType,
} from '../types';

import { getEntityIdInScope, mapCreatedDefinitionToNLPSearchType } from './utils';

type CreateDefinitionSupplierType = {
	createDefinition: CreateDefinitionFunctionType;
};

export const useCreateDefinitionSupplier = ({
	curatedDefinitionScopeConfig,
}: {
	curatedDefinitionScopeConfig: CuratedDefinitionScopeConfigType;
}): CreateDefinitionSupplierType => {
	const { commonAttributes, sourceProduct } = useAIAnswerContext();

	const createDefinition = useCallback(
		async ({
			keyPhrase,
			editedDefinition,
			scope,
			referenceContentId,
			referenceUrl,
		}: CreateDefinitionFunctionArgs) => {
			const { cloudId } = commonAttributes;
			const { workspaceId, productScopes } = curatedDefinitionScopeConfig;
			const entityIdInScope = getEntityIdInScope({
				selectedScope: scope,
				cloudId,
				productScopes,
			});

			// check needed to account for Spaces being added as sources and submitted
			const contentId =
				'contentARI' in productScopes && referenceContentId === entityIdInScope
					? productScopes.contentARI
					: referenceContentId;

			return fetchAggResponse<CreateDefinitionMutationVariables, CreateDefinitionMutationType>({
				graphQuery: CreateDefinitionMutation,
				variables: {
					workspaceId: workspaceId || '',
					keyPhrase,
					editedDefinition,
					scope,
					entityIdInScope,
					referenceContentId: contentId,
					referenceUrl,
				},
				headers: createAtlAttributionHeader({
					atlWorkspaceId: workspaceId,
					tenantId: cloudId,
					product: sourceProduct ?? '',
				}),
			}).then((response) => {
				if (response?.data?.knowledgeDiscovery.createDefinition?.success) {
					const definitionDetails =
						response.data.knowledgeDiscovery.createDefinition.definitionDetails;
					return (
						(definitionDetails && mapCreatedDefinitionToNLPSearchType(definitionDetails)) || {
							nlpResult: '',
							format: NLPSearchResultFormat.PLAINTEXT,
							uniqueSources: [],
							errorState: null,
							disclaimer: null,
						}
					);
				} else {
					throw new Error('createDefinition mutation unsuccessful');
				}
			});
		},
		[commonAttributes, curatedDefinitionScopeConfig, sourceProduct],
	);

	return { createDefinition };
};
